import React, { useMemo } from "react";

import { Stack, Typography } from "@mui/material";
import { AsyncContentMui } from "components/Async-content/AsyncContentMui";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { IPortfolioConstructionCompareGraph } from "store/types";

interface Props {
  loading: boolean;
  data?: IPortfolioConstructionCompareGraph;
}

export const CompareGraph: React.FC<Props> = ({ loading, data }) => {
  const {
    graphs: { categories = [], series = [] }
  } = data || { graphs: { categories: [], series: [] } };

  const groupPadding = useMemo(() => {
    if (categories.length <= 1) {
      return 0.445;
    }
    if (categories.length <= 2) {
      return 0.38;
    }
    if (categories.length <= 3) {
      return 0.315;
    }
    return 0.27;
  }, [categories]);

  const options: Highcharts.Options = {
    chart: {
      type: "column",
      height: 250,
      backgroundColor: "transparent",
      plotBorderColor: "#DBE8F5",
      plotBorderWidth: 1,
      scrollablePlotArea: {
        minWidth: categories.length * 250,
        scrollPositionX: 0
      }
    },
    title: {
      text: undefined
    },
    credits: {
      enabled: false
    },
    exporting: {
      enabled: false
    },
    xAxis: {
      categories,
      lineWidth: 0,
      tickWidth: 0,
      labels: {
        style: {
          color: "#496682",
          fontSize: "12px"
        }
      }
    },
    yAxis: {
      title: {
        text: undefined
      },
      gridLineColor: "#ECF1F8",
      lineWidth: 0,
      tickWidth: 0,
      labels: {
        enabled: true,
        style: {
          color: "#496682",
          fontSize: "12px"
        },
        formatter: function (this: Highcharts.AxisLabelsFormatterContextObject) {
          const value = this.value as number;

          if (value >= 1000000000) {
            return `$${(value / 1000000000).toFixed(0)}B`;
          } else if (value >= 1000000) {
            return `$${(value / 1000000).toFixed(0)}M`;
          } else {
            return `$${value.toLocaleString()}`;
          }
        }
      }
    },
    tooltip: {
      // eslint-disable-next-line quotes
      pointFormat: '<span style="color:{point.color}">●</span> {series.name}: <b>${point.y:,.2f}</b>'
    },
    legend: {
      enabled: true,
      align: "center",
      verticalAlign: "bottom",
      layout: "horizontal",
      itemStyle: {
        color: "#496682",
        fontSize: "12px",
        fontWeight: "400"
      }
    },
    plotOptions: {
      column: {
        pointWidth: 50,
        groupPadding: groupPadding,
        borderRadius: 2,
        minPointLength: 5,
        stacking: undefined
      }
    },
    series: series.map((s) => ({
      ...s,
      type: "column"
    }))
  };

  return (
    <Stack
      sx={{
        padding: "16px",
        width: "100%",
        height: "100%",
        gap: "20px",
        backgroundColor: "white",
        borderRadius: "8px",
        boxShadow: "0 1px 2px 0 #1212171a",
      }}
    >
      <Stack flexDirection="row" justifyContent="flex-start" alignItems="center">
        <Typography fontSize="18px" fontWeight="500" color="#071F35">
          By Asset Class
        </Typography>
      </Stack>
      <AsyncContentMui isLoading={loading}>
        <HighchartsReact highcharts={Highcharts} options={options} />
      </AsyncContentMui>
    </Stack>
  );
};
